import { useRef, useState } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import Textarea from '@mui/joy/Textarea';
import { Alert, Badge, IconButton, Sheet, Snackbar, Stack, Tooltip, Typography } from '@mui/joy';

import FormatBoldRoundedIcon from '@mui/icons-material/FormatBoldRounded';
import FormatItalicRoundedIcon from '@mui/icons-material/FormatItalicRounded';
import StrikethroughSRoundedIcon from '@mui/icons-material/StrikethroughSRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import InsertPhotoRoundedIcon from '@mui/icons-material/InsertPhotoRounded';
import InsertEmoticonRoundedIcon from '@mui/icons-material/InsertEmoticonRounded';




export default function MessageInput(props) {
  const {
    textAreaValue,
    setTextAreaValue,
    onSubmit,
    onAttach,
    // === НОВЫЕ ПРОПСЫ ===
    editingMessage,         // объект редактируемого сообщения или null
    onCancelEdit,           // callback отмены редактирования
    maxLength,              // лимит символов для пациента, null/undefined = без лимита
    disabled = false,       // блокировка ввода (например, профиль заблокирован)
    sending = false,
    disabledReason = '',    // причина для подсказки
  } = props;
  const textAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // === Состояние лимита символов ===
  const overLimit = maxLength != null && (textAreaValue?.length || 0) > maxLength;
  const nearLimit = maxLength != null && !overLimit && (maxLength - (textAreaValue?.length || 0)) <= 200;

  const isEditing = !!editingMessage;

  // Функция проверки файлов
  const validateFiles = (files) => {
    const MAX_FILE_SIZE = (props.maxFileSizeMb || 50) * 1024 * 1024; // дефолт 50 МБ (совпадает с CHAT_MAX_FILE_SIZE сервера)
    const MAX_FILE_COUNT = 5;
    
    const _d = [...files, ...attachments]
    // Проверка количества файлов
    if (_d?.length > MAX_FILE_COUNT) {
      setSnackbar({
        open: true,
        message: `Максимум ${MAX_FILE_COUNT} файлов`,
        severity: 'danger'
      });
      return false;
    }
    
    // Проверка размера каждого файла
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        const mb = MAX_FILE_SIZE / (1024 * 1024);
        setSnackbar({
          open: true,
          message: `Файл "${file.name}" превышает ${mb} МБ`,
          severity: 'danger'
        });
        return false;
      }
    }
    
    return true;
  };

  const submitBusyRef = useRef(false);
  const handleClick = async () => {
    if (disabled || submitBusyRef.current) return;
    if (overLimit) {
      setSnackbar({ open: true, message: `Сообщение слишком длинное. Лимит ${maxLength} символов.`, severity: 'danger' });
      return;
    }
    if (isEditing ? !textAreaValue.trim() : (!attachments.length && !textAreaValue.trim())) return;
    submitBusyRef.current = true;
    try {
      const sent = await onSubmit();
      if (sent === true && !isEditing) { setAttachments([]); onAttach?.([]); }
    } finally { submitBusyRef.current = false; }
  };
  
  const handleAttachClick = (type = 'any') => {
    if (type === 'image') {
      imageInputRef?.current?.click();
    } else {
      fileInputRef?.current?.click();
    }
  };

  const handleFileSelect = (event, type = 'any') => {
    const files = Array.from(event.target.files);
    
    if (files.length > 0) {
      // Проверяем файлы перед добавлением
      if (!validateFiles(files)) {
        event.target.value = '';
        return;
      }
      
      if (type === 'image') {
        // Фильтруем только изображения
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        onAttach([...attachments, ...imageFiles]);
        setAttachments(prev => [...prev, ...imageFiles]);
      } else {
        onAttach([...attachments, ...files]);
        setAttachments(prev => [...prev, ...files]);
      }
    }
    
    // Сбрасываем значение input для возможности выбора тех же файлов снова
    event.target.value = '';
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        // Shift + Enter - перенос строки
        event?.preventDefault();
        setTextAreaValue(prev => prev + '\n');
      } else {
        // Enter без Shift - отправка сообщения
        event?.preventDefault();
        handleClick();
      }
    }
  };

  const removeAttachment = (index) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
    // Сообщаем родительскому компоненту об удалении файла
    onAttach(newAttachments);
  };

  const handleTextareaChange = (event) => {
    // Автоматическое увеличение высоты textarea
    setTextAreaValue(event.target.value);
    
    // // Автоматическое изменение высоты
    // setTimeout(() => {
    //   if (textAreaRef.current) {
    //     textAreaRef.current.style.height = 'auto';
    //     textAreaRef.current.style.height = Math.min(textAreaRef.current.scrollHeight, 200) + 'px';
    //   }
    // }, 0);
  };


  return (
    <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.25, pb: 'max(12px, env(safe-area-inset-bottom))', flexShrink: 0, bgcolor: 'background.surface', borderTop: '1px solid', borderColor: 'divider' }}>
      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Typography level='title-lg' sx={{ textAlign: "center", width: "100%" }} color={snackbar.severity}>
          {snackbar.message}
        </Typography>
      </Snackbar>

      {/* === Баннер режима редактирования === */}
      {isEditing && (
        <Sheet
          variant="soft"
          color="primary"
          sx={{
            mb: 1,
            p: 1,
            borderRadius: 'lg',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography level="body-xs" color="primary" sx={{ fontWeight: 'lg' }}>
              Редактирование сообщения
            </Typography>
            <Typography
              level="body-xs"
              color="neutral"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {editingMessage?.content}
            </Typography>
          </Box>
          <IconButton size="sm" variant="plain" color="neutral" onClick={onCancelEdit}>
            ✕
          </IconButton>
        </Sheet>
      )}

      {/* === Если профиль заблокирован — показываем алерт и не даём писать === */}
      {disabled && !sending && (
        <Alert color="danger" variant="soft" sx={{ mb: 1, borderRadius: 'lg' }}>
          Отправка сообщений недоступна{disabledReason ? `: ${disabledReason}` : ''}
        </Alert>
      )}

      {/* Превью вложений */}
      {attachments.length > 0 && !isEditing && (
        <Sheet 
          variant="outlined" 
          sx={{ 
            mb: 1, 
            p: 1, 
            borderRadius: 'lg',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          {attachments.map((file, index) => (
            <Badge
              key={index}
              color="neutral"
              badgeContent="×"
              onClick={() => removeAttachment(index)}
              sx={{ 
                cursor: 'pointer',
                '& .MuiBadge-badge': {
                  backgroundColor: 'danger.500',
                  color: 'white',
                  cursor: 'pointer'
                }
              }}
            >
              <Box
                sx={{
                  p: 1,
                  borderRadius: 'md',
                  backgroundColor: 'background.level1',
                  fontSize: 'sm',
                  maxWidth: 120,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {file.type.startsWith('image/') ? '🖼️ ' : '📄 '}
                {file.name}
              </Box>
            </Badge>
          ))}
        </Sheet>
      )}

      <Sheet 
        variant="outlined"
        color="neutral"
        sx={{
          p: 0.75,
          px: 1,
          gap: 1,
          borderRadius: '16px',
          bgcolor: 'background.surface',
          borderColor: 'divider',
          '&:focus-within': { borderColor: 'primary.400' },
        }}
      >
        <Stack spacing={0.5} direction={'row'} sx={{ alignItems: 'center' }}>
          {/* Скрытые input'ы для файлов */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={(e) => handleFileSelect(e, 'any')}
            multiple
          />
          <input
            type="file"
            ref={imageInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={(e) => handleFileSelect(e, 'image')}
            multiple
          />

          {/* Кнопки действий */}
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={isEditing ? 'В режиме редактирования вложения недоступны' : 'Прикрепить файл'} placement="top">
              <span>
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={() => handleAttachClick('any')}
                  disabled={disabled || isEditing}
                >
                  <AttachFileRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* <Tooltip title="Прикрепить фото" placement="top">
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => handleAttachClick('image')}
              >
                <InsertPhotoRoundedIcon />
              </IconButton>
            </Tooltip> */}

            {/* <Tooltip title="Смайлики" placement="top">
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
              >
                <InsertEmoticonRoundedIcon />
              </IconButton>
            </Tooltip> */}
          </Stack>

          {/* Поле ввода */}
          <FormControl sx={{ flexGrow: 1 }}>
            <Textarea
              placeholder={isEditing ? 'Введите новый текст сообщения...' : 'Введите сообщение...'}
              aria-label="Сообщение"
              ref={textAreaRef}
              color='neutral'
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              value={textAreaValue}
              minRows={1}
              maxRows={6}
              disabled={disabled}
              slotProps={{ textarea: { autoComplete: 'off', autoCorrect: 'off', spellCheck: false } }}
              sx={{
                borderRadius: '10px',
                p: 0,
                border: 'none',
                boxShadow: 'none',
                bgcolor: 'background.surface',
                resize: 'none',
                '&:focus-within': {
                  boxShadow: 'none',
                },
                '& textarea': {
                  p: 1,
                  maxHeight: '120px',
                  // === Фикс задачи 7: курсор и текст сливались с фоном в светлой теме на мобилке ===
                  color: 'var(--joy-palette-text-primary)',
                  caretColor: 'var(--joy-palette-text-primary)',
                  WebkitTextFillColor: 'var(--joy-palette-text-primary)',
                }
              }}
            />
          </FormControl>

          {/* Кнопка отправки */}
          <Stack>
            <Tooltip title={isEditing ? 'Сохранить (Enter)' : 'Отправить (Enter)'} placement="top">
              <IconButton
                size="sm"
                color="primary"
                variant={textAreaValue.trim() || attachments.length > 0 ? "solid" : "soft"}
                onClick={handleClick}
                disabled={disabled || overLimit || (!textAreaValue.trim() && attachments.length === 0)}
                sx={{
                  borderRadius: '12px',
                  minWidth: 36, minHeight: 36,
                  alignSelf: 'flex-end',
                  transition: 'background-color 0.2s',
                }}
              >
              <SendRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Sheet>

      {/* Подсказка внизу + счётчик символов (для пациента) */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 0.5,
        px: 0.5,
      }}>
        <Typography level="body-xs" color="neutral" sx={{ display: { xs: 'none', sm: 'block' } }}>
          Enter — отправить · Shift+Enter — новая строка
        </Typography>
        {maxLength != null && (
          <Typography
            level="body-xs"
            color={overLimit ? 'danger' : (nearLimit ? 'warning' : 'neutral')}
            sx={{ fontWeight: overLimit ? 'lg' : 'md' }}
          >
            {textAreaValue?.length || 0} / {maxLength}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
